import { Injectable } from '@angular/core';
import { TeamImprovementId } from '../constants-types/StrategyConstants';
import {
    Group,
    GroupValidateResponse,
    SharedGroupValidationConflictItem,
    SharedGroupValidationConflictFolder,
    GroupUpdate,
    GroupItemUpdate,
    DreamTeamInsights,
    DreamTeamActionsDefinition
} from '../core-data-model/Group.model';
import {
    TeamServerEntry,
    TeamPatch,
    SharedGroupsGetUsersForGroupServer,
    SharedGroupsValidateServer,
    SharedGroupsUsersFoldersPost,
    TeamItemPatch,
    DreamTeamGetTeamInsightsServer,
    DreamTeamGetTeamActionsDefinitionServer
} from '../core-data-model/GroupsApi.serverinterface';
import { User } from '../core-data-model/User.model';
import { Util } from '../core-utilities/Util';
import { UserFactory } from '../object-factory/User.factory';

@Injectable({
    providedIn: 'root'
})
export class GroupTransformers {
    constructor(
        private userFactory: UserFactory,
        private util: Util
    ) {}
    transformServerToClient(serverTeam: TeamServerEntry): Group {
        return new Group({
            name: serverTeam.teamName || serverTeam.groupName,
            teamFunctionType: serverTeam.teamFunctionType,
            otherTeamFunction: serverTeam.otherTeamFunction,
            crossFunctionId: serverTeam.crossFunctionId,
            // converting string of comma separated TeamImprovementIds to TeamImprovementId[]
            teamImprovementIds: serverTeam.teamImprovementIds
                ? serverTeam.teamImprovementIds
                      .split(',')
                      .map((id) => Number(id))
                : [TeamImprovementId.Unknown],
            otherTeamImprovement: serverTeam.otherTeamImprovement,
            isStrategyAutoSelectionEnabled:
                serverTeam.isStrategyAutoSelectionEnabled,
            id: serverTeam.teamId || serverTeam.groupId,
            peopleAndJobCount:
                serverTeam.teamItemsCount || serverTeam.groupItemsCount,
            groupOwnerId: serverTeam.teamOwnerId || serverTeam.groupOwnerId,
            groupOwner: this.userFactory.new_UserStub({
                id: serverTeam.ownerInfo.userId,
                email: serverTeam.ownerInfo.email,
                firstName: serverTeam.ownerInfo.name.first,
                lastName: serverTeam.ownerInfo.name.last
            }),
            isEditableByUser: serverTeam.isEditableByUser,
            isEditableBySharedUsers: serverTeam.isEditableBySharedUsers,
            modifiedTime: this.util.newDate(serverTeam.modifiedTime),
            createdTime: this.util.newDate(serverTeam.createdTime)
        });
    }

    transformClientToServerPatch(team: GroupUpdate): TeamPatch {
        return {
            teamName: team.name,
            teamFunctionType: team.teamFunctionType,
            otherTeamFunction: team.otherTeamFunction,
            crossFunctionId: team.crossFunctionId,
            teamImprovementIds: team.teamImprovementIds,
            otherTeamImprovement: team.otherTeamImprovement,
            isStrategyAutoSelectionEnabled: team.isStrategyAutoSelectionEnabled,
            isEditableBySharedUsers: team.isEditableBySharedUsers
        };
    }

    transformClientToServerPatchTeamItem(
        teamItem: GroupItemUpdate
    ): TeamItemPatch {
        return {
            personId: teamItem.personId,
            isManager: teamItem.isManager
        };
    }

    transformValidateResponseServerToClient(
        groupValidateResponses: SharedGroupsValidateServer
    ): GroupValidateResponse[] {
        if (groupValidateResponses) {
            const problemItems: SharedGroupValidationConflictItem[] =
                groupValidateResponses.checkUsersAccessToGroupsReturnsDtos.map(
                    (validate) => {
                        const processed: SharedGroupValidationConflictItem = {
                            id: validate.jobId
                                ? validate.jobId
                                : validate.personId,
                            folderId: validate.folderId,
                            name: validate.jobId
                                ? validate.jobName
                                : `${validate.personFirstName} ${validate.personLastName}`,
                            type: validate.jobId ? 'job' : 'person'
                        };
                        return processed;
                    }
                );

            const problemFolders: SharedGroupValidationConflictFolder[] =
                groupValidateResponses.checkUsersAccessToGroupsReturnsDtos.map(
                    (validate) => ({
                        id: validate.folderId,
                        name: validate.folderName,
                        path: validate.folderPath,
                        problemItems: problemItems.filter(
                            (item) => item.folderId == validate.folderId
                        )
                    })
                );

            const validationErrors: GroupValidateResponse[] =
                groupValidateResponses.checkUsersAccessToGroupsReturnsDtos.map(
                    (validate) => ({
                        groupId: validate.groupId,
                        userId: validate.userId,
                        userName: `${validate.userFirstName} ${validate.userLastName}`,
                        problemFolders: problemFolders
                    })
                );
            return validationErrors;
        }
    }

    transformUserAccessClientToServer(
        groupValidate: GroupValidateResponse
    ): SharedGroupsUsersFoldersPost {
        const userFolderAssociation = groupValidate.problemFolders.map(
            (folder) => ({
                userCompanyMyGroupGuid: groupValidate.groupId,
                userGuid: groupValidate.userId,
                itemFolderGuid: folder.id
            })
        );

        return {
            sharedGroupsUsersFoldersParameters: userFolderAssociation
        };
    }

    transformUserAccessServerToClient(
        sharedGroupUserAccess: SharedGroupsGetUsersForGroupServer
    ): User[] {
        const usersAccess = sharedGroupUserAccess.returnItems.map((user) =>
            this.userFactory.new_User({
                id: user.userId,
                firstName: user.userFirstName,
                lastName: user.userLastName,
                email: user.userEmail,
                isThirdPartyUser: user.isThirdPartyUser,
                role: user.userRole
            })
        );
        return usersAccess;
    }

    transformUnshareUserClientToServer(user: User, groupId: string) {
        return {
            sharedGroupsUsersFoldersParameters: [
                {
                    userCompanyMyGroupGuid: groupId,
                    userGuid: user.id
                }
            ]
        };
    }

    transformTeamInsightsServerToClient(
        dreamTeamInsights: DreamTeamGetTeamInsightsServer
    ): DreamTeamInsights {
        return {
            title: dreamTeamInsights.title,
            version: dreamTeamInsights.version,
            teamSizes: dreamTeamInsights.teamSizes,
            teamStrategyFrequencies: dreamTeamInsights.teamStrategyFrequencies,
            teamTypeFrequencies: dreamTeamInsights.teamTypeFrequencies,
            teamActionRankings: dreamTeamInsights.teamActionRankings
        };
    }

    transformTeamActionsDefinitionServerToClient(
        dreamTeamActionsDefinition: DreamTeamGetTeamActionsDefinitionServer
    ): DreamTeamActionsDefinition {
        return {
            teamActionsByTeamType:
                dreamTeamActionsDefinition.teamActionsByTeamType
        };
    }
}
